import React, { useState } from 'react';
import { api_with_method } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';

interface RenewMembershipButtonProps {
  onClose: () => void;
  set_notification_variable?: (show: boolean, type: string, message: string) => void;
  studentName?: string;
}

function RenewMembershipButton({
  onClose,
  set_notification_variable,
  studentName
}: RenewMembershipButtonProps) {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const initialUUID = params?.get('id') || '';

  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');

  const handleConfirm = async () => {
    if (!reason.trim()) {
      return;
    }
    setLoading(true);
    try {
      const apiUrl = await get_api_url('renew_recurring_subscription_v1');
      const payload = {
        student_uuid: initialUUID,
        reason
      };

      await api_with_method('post', apiUrl, payload, true);
      if (set_notification_variable) {
        set_notification_variable(true, 'success', 'Membership renewal successful');
      }
      onClose();
    } catch (error) {
      const message = (error as any)?.response?.data?.message || 'Renewal failed';
      if (set_notification_variable) {
        set_notification_variable(true, 'error', message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-2">
      <div className="mb-3">
        <h2 className="font-bold text-red-500 text-center text-lg">
          Are you sure you want to renew {studentName ? `${studentName}'s` : "this user's"}{' '}
          membership?
        </h2>
        <label
          className="block text-sm font-medium text-gray-700 mb-1.5"
          style={{ textTransform: 'unset' }}
          htmlFor="renew-membership-reason"
        >
          Reason{' '}
          <span className="text-red-500" aria-hidden>
            *
          </span>
        </label>
        <textarea
          id="renew-membership-reason"
          className="min-h-[100px] w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-500 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-y"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Student added credits for membership renewal, payment issue resolved…"
          aria-required
        />
        <p className="mt-1 text-xs text-gray-500">
          Required for audit. This will be recorded in our system.
        </p>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="w-full sm:w-auto rounded-xl border-2 border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50"
          aria-label="Cancel and close"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!reason.trim() || loading}
          className={`w-full sm:w-auto rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm ${
            !reason.trim() || loading
              ? 'bg-emerald-400 cursor-not-allowed'
              : 'bg-emerald-600 hover:bg-emerald-700'
          }`}
          aria-label={loading ? 'Processing renewal' : 'Confirm renewal'}
        >
          {loading ? 'Processing…' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}

export default RenewMembershipButton;
